import apiClient from "@/api/client";
import { AuthUser, TenderBadgePreference } from "@/store/auth";

// ── Przekazywanie powiadomień ────────────────────────────────────────────────

export interface DelegationUser {
  id: string;
  full_name: string;
  role: string;
  role_label: string;
  job_title: string;
}

export type NotificationDelegationStatus = "pending" | "active" | "declined" | "revoked";

export interface NotificationDelegation {
  id: string;
  status: NotificationDelegationStatus;
  status_label: string;
  /** Tryb urlopowy: kopia odczytana u przejmującego oznacza oryginał jako przeczytany. */
  sync_read: boolean;
  delegator: DelegationUser;
  delegate: DelegationUser;
  created_at: string;
  responded_at: string | null;
  ended_at: string | null;
  ended_by_name: string | null;
}

/** Everything the settings section needs: my own open delegation (or null) and
 *  the requests / takeovers addressed to me. */
export interface NotificationDelegationState {
  outgoing: NotificationDelegation | null;
  incoming: NotificationDelegation[];
}

export interface DelegationCandidates {
  users: DelegationUser[];
  /** false → the account has no branch, so "same branch" cannot be resolved. */
  hasBranch: boolean;
}

const DELEGATIONS_URL = "/notification-delegations/";

export const notificationDelegationsApi = {
  state: async (): Promise<NotificationDelegationState> => {
    const res = await apiClient.get(DELEGATIONS_URL);
    return res.data.data;
  },

  candidates: async (): Promise<DelegationCandidates> => {
    const res = await apiClient.get(`${DELEGATIONS_URL}candidates/`);
    return { users: res.data.data ?? [], hasBranch: res.data.meta?.has_branch ?? true };
  },

  create: async (input: { delegate: string; sync_read: boolean }): Promise<NotificationDelegation> => {
    const res = await apiClient.post(DELEGATIONS_URL, input);
    return res.data.data;
  },

  /** Only the delegator, only while the delegation is open. */
  setSyncRead: async (id: string, sync_read: boolean): Promise<NotificationDelegation> => {
    const res = await apiClient.patch(`${DELEGATIONS_URL}${id}/`, { sync_read });
    return res.data.data;
  },

  accept: async (id: string): Promise<NotificationDelegation> => {
    const res = await apiClient.post(`${DELEGATIONS_URL}${id}/accept/`, {});
    return res.data.data;
  },

  decline: async (id: string): Promise<NotificationDelegation> => {
    const res = await apiClient.post(`${DELEGATIONS_URL}${id}/decline/`, {});
    return res.data.data;
  },

  revoke: async (id: string): Promise<NotificationDelegation> => {
    const res = await apiClient.post(`${DELEGATIONS_URL}${id}/revoke/`, {});
    return res.data.data;
  },
};

// ── Moje uprawnienia ─────────────────────────────────────────────────────────

/** Uprawnienie z dodatkowej flagi konta, które użytkownik faktycznie ma.
 *  `auto` = z pełnionej funkcji (bez zapalonej flagi), `flag` = nadane. */
export interface GrantedPermission {
  key: string;
  group: string;
  label: string;
  description: string;
  source: "auto" | "flag";
}

export const settingsApi = {
  myPermissions: async (): Promise<GrantedPermission[]> => {
    const res = await apiClient.get("/auth/me/permissions/");
    return res.data.data ?? [];
  },


  updateTenderBadgePreferences: async (
    data: Partial<TenderBadgePreference>,
  ): Promise<TenderBadgePreference> => {
    const res = await apiClient.put("/tenders/badge-preferences/", data);
    return res.data.data;
  },

  updateProfile: async (data: {
    phone_number?: string;
    email?: string;
    receives_print_order_notifications?: boolean;
  }): Promise<AuthUser> => {
    const res = await apiClient.patch("/auth/me/", data);
    return res.data.data;
  },

  uploadAvatar: async (blob: Blob): Promise<AuthUser> => {
    const formData = new FormData();
    formData.append("profile_picture", blob, "avatar.jpg");
    const res = await apiClient.patch("/auth/me/", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data.data;
  },

  deleteAvatar: async (): Promise<AuthUser> => {
    const res = await apiClient.delete("/auth/me/avatar/");
    return res.data.data;
  },
};
